import { has, uuid } from "../global.functions";
import { CommonBaseModel } from "./common.model";

export enum RuleType {
    None = "",
    Eval = "EVAL",
    And = "AND",
    Or = "OR",
    If = "IF",
    Show = "SHOW",
    Hide = "HIDE",
    Validate = "VALIDATE",
}

/** A field scope enumeration. */
export enum FieldScope {
    Application = "application",
    Posted = "posted",
    Unknown = "unknown",
}

/** Denotes a comparison type. */
export enum ComparisonType {
    Value = "Value",
    Field = "Field",
}

/** An operator enumeration. */
export enum Operator {
    EQ = "EQ",
    NE = "NE",
    GT = "GT",
    GTE = "GTE",
    LT = "LT",
    LTE = "LTE",
    IN = "IN",
    NIN = "NIN",
    CONTAINS = "CONTAINS",
    NOTCONTAINS = "NOTCONTAINS",
}

export enum RuleOperatorNames {
    EQ = "is Equal to",
    NE = "is Not equal to",
    GT = "is Greater than",
    GTE = "is Greater than or Equal to",
    LT = "is Less than",
    LTE = "is Less than or Equal to",
    IN = "is In",
    NIN = "is Not in",
    CONTAINS = "Contains",
    NOTCONTAINS = "Does not contain",
}

export interface IValidationError {
    position: string | undefined;
    message: string | undefined;
    hide: boolean;

}

export class ValidationError {
    position: string = '';
    message: string = '';
    hide: boolean = false;

    constructor(data?: IValidationError) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.position = _data["Position"];
            this.message = _data["Message"];
            this.hide = <boolean>_data["Hide"];
        }
    }

    static fromJs(data: any) {
        data = typeof data === 'object' ? data : {};
        let result = new ValidationError();
        result.init(data);
        return result;
    }

    toJSON(data?: any) { }
}


/** A comparison definition. */
export class CompareDefinition implements ICompareDefinition {
    compareTo!: ComparisonType;
    operator!: Operator;
    /** Gets or sets the field to compare. */
    field!: string | undefined;
    /** Gets or sets the sub-field to compare against, used in cases such as
Geolocation latitude / longitude which reside in the one control field. */
    subField!: string | undefined;
    scope!: FieldScope;
    /** Gets or sets the field or field value to compare against. */
    value!: any | undefined;

    constructor(data?: ICompareDefinition) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any): this
    {
        if (_data) {
            this.compareTo = _data["CompareTo"];
            this.operator = _data["Operator"];
            this.field = _data["Field"];
            this.subField = _data["SubField"];
            this.scope = _data["Scope"];
            this.value = _data["Value"];
        }

        return this;
    }

    static fromJS(data: any): CompareDefinition {
        data = typeof data === 'object' ? data : {};
        return (new CompareDefinition()).init(data);
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["CompareTo"] = this.compareTo;
        data["Operator"] = this.operator;
        data["Field"] = this.field;
        data["SubField"] = this.subField;
        data["Scope"] = this.scope;
        data["Value"] = this.value;
        return data;
    }
}

/** A comparison definition. */
export interface ICompareDefinition {
    compareTo: ComparisonType;
    operator: Operator;
    /** Gets or sets the field to compare. */
    field: string | undefined;
    /** Gets or sets the sub-field to compare against, used in cases such as
Geolocation latitude / longitude which reside in the one control field. */
    subField: string | undefined;
    scope: FieldScope;
    /** Gets or sets the field or field value to compare against. */
    value: any | undefined;
}

/** An interface for a truth condition. */
export class TruthCondition extends CommonBaseModel implements ITruthCondition {
    _id: string = uuid(); // for tracking and drag/drop
    _parent: TruthCondition = null;

    ruleType!: RuleType;
    value!: string | string[] | TruthCondition | TruthCondition[] | CompareDefinition;

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);

        // this.ruleType = RuleType[_data["RuleType"] as keyof typeof RuleType] || RuleType.None;
        this.ruleType = _data["RuleType"].toUpperCase() || RuleType.None;
        const value = _data["Value"];

        if (this.ruleType === RuleType.Eval) {
            this.value = (new CompareDefinition()).init(value);
        }
        else if (typeof value === 'string') {
            this.value = value;
        }
        else if (Array.isArray(value)) {
            const rules = [];
            value.forEach(item =>
                rules.push((typeof item === 'string')
                    ? item
                    : (new TruthCondition()).init(item)));
            this.value = rules;
        }
        else {
            this.value = undefined;
        }

        return this;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["RuleType"] = this.ruleType;

        if (typeof this.value === 'string') {
            data["Value"] = this.value;
        } else if (Array.isArray(this.value)) {
            let rules = [];
            this.value.forEach(rule => rules.push((rule as TruthCondition).toJSON()));
            data["Value"] = rules;
        } else {
            data["Value"] = <any>undefined;
        }

        return data;
    }
}

/** An interface for a truth condition. */
export interface ITruthCondition {
    ruleType: RuleType;
    value: string | string[] | TruthCondition | TruthCondition[] | CompareDefinition;
    _parent: ITruthCondition;
}

export interface IConditionWithName {
    ruleName: string | undefined;
}

export class BaseTruthCondition extends TruthCondition implements IConditionWithName {
    ruleName: string;
    ruleType: RuleType.Hide | RuleType.Show | RuleType.Validate | RuleType.None;
    value: TruthCondition[] = [];

    init(_data?: any) {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);
        return this;
    }
}

/** A logical AND operator. */
export class LogicalAndTruthCondition extends CommonBaseModel {
    /** Represents a strongly typed list of iApply.Model.Rules.ITruthConditions. */
    value!: TruthCondition[];
    ruleType!: RuleType;

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);

        if (Array.isArray(_data["value"])) {
            this.value = [] as any;
            for (let item of _data["value"]) {
                this.value!.push((new TruthCondition()).init(item));
            }
        }
        this.ruleType = _data["ruleType"];

        return this;
    }

    static fromJS(data: any): LogicalAndTruthCondition {
        data = typeof data === 'object' ? data : {};
        let result = new LogicalAndTruthCondition();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        if (Array.isArray(this.value)) {
            data["value"] = [];
            for (let item of this.value)
                data["value"].push(item.toJSON());
        }
        data["ruleType"] = this.ruleType;
        return data;
    }
}

/** An IF condition */
// export class IfTruthCondition extends LogicalAndTruthCondition
// {}

export interface ITruthConditionGroup {
    value: TruthCondition[];
}

// export class LogicalAndTruthCondition implements ILogicalAndTruthCondition {
export class TruthConditionGroup extends TruthCondition implements ITruthConditionGroup {
    ruleType: RuleType.And | RuleType.Or = RuleType.And;
    value: TruthCondition[] = [];
}

// /**
//  * Determines whether or not a rule is an EVAL
//  * @param rule
//  * @returns
//  */
export function isTruthConditionGroup(rule: any): rule is ITruthConditionGroup {
    return [RuleType.And, RuleType.Or].includes(rule.ruleType);
}

export class VisibilityTruthCondition extends BaseTruthCondition {
    ruleType: RuleType.Show | RuleType.Hide | RuleType.None;
}

export interface IValidateTruthCondition {
    ruleType: RuleType.Validate;
    error: ValidationError;
}

export class ValidateTruthCondition extends BaseTruthCondition implements IValidateTruthCondition {
    ruleType: RuleType.Validate = RuleType.Validate;
    error: ValidationError = new ValidationError();

    static fromJS(data: any): ValidateTruthCondition {
        data = typeof data === 'object' ? data : {};
        let result = new ValidateTruthCondition();
        result.init(data);
        return result;
    }

    init(_data?: any) {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);

        return this;
    }

    toJSON(data?: any) {
        data = super.toJSON(data);
        data["error"] = this.error ? this.error.toJSON() : <any>null;
        return data;
    }
}

export interface ICompareTruthCondition {
    fieldScope: FieldScope;
    compareTo: ComparisonType;
    operator: Operator;
    field: string | undefined;
    subField: string | undefined
    value: string | string[];
}

export class CompareTruthCondition extends TruthCondition implements ICompareTruthCondition {
    ruleType: RuleType.Eval = RuleType.Eval;
    fieldScope: FieldScope = FieldScope.Unknown;
    compareTo: ComparisonType = ComparisonType.Field;
    operator: Operator = Operator.EQ;
    field: string = '';
    subField: string = '';
    value: string | string[] = '';

    init(_data?: any):this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }
        _data = _data["response"] ? _data["response"] : _data;

        super.init(_data);
        return this;
    }

    static fromJS(data: any): CompareTruthCondition {
        data = typeof data === 'object' ? data : {};
        let result = new CompareTruthCondition();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};

        // parent class inheritance
        data = super.toJSON(data);

        data["RuleType"] = this.ruleType;
        data["Value"] = this.value;

        data[""] = this.fieldScope;
        data[""] = this.compareTo;
        data[""] = this.operator;
        data[""] = this.field;
        data[""] = this.subField;

        // if (typeof this.value === 'string') {
        //     data["Value"] = this.value;
        // } else if (Array.isArray(this.value)) {
        //     let rules = [];
        //     this.value.forEach(rule => rules.push(rule));
        //     data["Value"] = rules;
        // } else {
        //     data["Value"] = <any>undefined;
        // }

        return data;
    }
}

export function isCompareRule(rule: any): rule is ICompareTruthCondition {
    return rule && has(rule, 'fieldScope') && rule.ruleType == RuleType.Eval;
}
